import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "destructive" | "ghost" | "record";
type Size = "sm" | "md" | "lg";

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-[#2563eb] text-white hover:bg-[#1d4ed8] active:bg-[#1e40af] disabled:bg-[#cbd5e1] disabled:text-white disabled:cursor-not-allowed",
  secondary:
    "bg-white text-[#1a1a1a] border border-[#e2e8f0] hover:bg-[#f8fafc] hover:border-[#cbd5e1] disabled:bg-[#f8fafc] disabled:text-[#94a3b8] disabled:cursor-not-allowed",
  destructive:
    "bg-white text-[#991b1b] border border-[#fecaca] hover:bg-[#fef2f2] disabled:opacity-60 disabled:cursor-not-allowed",
  ghost:
    "bg-transparent text-[#475569] hover:bg-[#f8fafc] disabled:opacity-60 disabled:cursor-not-allowed",
  record:
    "bg-[#ef4444] text-white hover:bg-[#dc2626] disabled:opacity-60 disabled:cursor-not-allowed",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-9 px-3 text-[13px] gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-[15px] gap-2",
};

interface ButtonBaseProps {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  children?: ReactNode;
  className?: string;
}

type ButtonProps = ButtonBaseProps & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "lg",
    fullWidth,
    loading,
    leadingIcon,
    trailingIcon,
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center rounded-[12px] font-semibold transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb] focus-visible:ring-offset-2",
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {leadingIcon}
      {loading ? "…" : children}
      {trailingIcon}
    </button>
  );
});

interface LinkButtonProps extends ButtonBaseProps {
  to: LinkProps["to"];
  state?: LinkProps["state"];
  replace?: boolean;
  target?: string;
  rel?: string;
}

export function LinkButton({
  variant = "primary",
  size = "lg",
  fullWidth,
  leadingIcon,
  trailingIcon,
  className,
  children,
  to,
  state,
  replace,
  target,
  rel,
}: LinkButtonProps) {
  return (
    <Link
      to={to}
      state={state}
      replace={replace}
      target={target}
      rel={rel}
      className={cn(
        "inline-flex items-center justify-center rounded-[12px] font-semibold transition-colors duration-200 no-underline",
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && "w-full",
        className,
      )}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </Link>
  );
}
